import React, { useState, useEffect, useContext, useCallback } from "react";
import { ChevronLeft, ChevronRight, Download, Trash2, MapPin, Video } from "lucide-react";

import { TAG_META } from "./tagMeta";
import { useTaggedMessages } from "../../context/TaggedMessagesContext";
import { AuthContext } from "../../context/AuthContext";
import { toast } from "../ui/Toast";
import MeetingModal from "./MeetingModal";
import { useCallContext } from "../../context/CallContext";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const ACCENT   = "#00FF99";

// ── Compact-calendar helpers (replace react-big-calendar's grid/toolbar) ──
const DOW_LABELS = ["M", "T", "W", "T", "F", "S", "S"];
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Monday = 0
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - day);
  return x;
};
const sameDate = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth()    === b.getMonth()    &&
  a.getDate()     === b.getDate();
const startOfMonth     = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfNextMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 1);
const startOfDay       = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
// "8 am" / "12 pm" / "5 pm" — matches the spec's hour-row labels.
const hourLabel = (h) => {
  const ap = h < 12 ? "am" : "pm";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh} ${ap}`;
};
// "13 – 19 July" (same month) / "30 June – 6 July" (crosses a month).
const weekRangeLabel = (ws) => {
  const we = addDays(ws, 6);
  const M = { month: "long" };
  return ws.getMonth() === we.getMonth() && ws.getFullYear() === we.getFullYear()
    ? `${ws.getDate()} – ${we.getDate()} ${ws.toLocaleDateString([], M)}`
    : `${ws.getDate()} ${ws.toLocaleDateString([], M)} – ${we.getDate()} ${we.toLocaleDateString([], M)}`;
};

// Tag metadata (icon + color per tag type) now lives in ./tagMeta so the
// composer's TagSelector renders the same glyphs/colors as this rail.

const niceTime = (iso) => {
  if (!iso) return "";
  const d   = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now - d) / 60000);
  if (diff < 1)    return "just now";
  if (diff < 60)   return `${diff}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return d.toLocaleDateString([], { day: "2-digit", month: "short" });
};

// ─────────────────────────────────────────────────────────────
// TAGGED SECTION
// ─────────────────────────────────────────────────────────────

function TaggedSection({ tagged, onView, onDone, onDelete }) {
  if (tagged.length === 0) return (
    <p style={{ color: "rgba(255,255,255,0.2)", fontSize: 11, textAlign: "center", padding: "8px 0" }}>
      No tagged messages yet
    </p>
  );
  return (
    <div style={{
      maxHeight: 260, overflowY: "auto", display: "flex", flexDirection: "column",
      paddingRight: 2, scrollbarWidth: "thin", scrollbarColor: "rgba(0,255,153,0.2) transparent",
    }}>
      {tagged.map((m) => {
        const tm = TAG_META[m.tag];
        if (!tm) return null;
        const TagIcon = tm.Icon;
        const isUrgent = m.tag === "urgent";
        return (
          <div key={m.id} style={{
            background: "#131313",
            border: `1px solid ${isUrgent && !m.is_done ? "rgba(255,95,86,0.35)" : "rgba(255,255,255,0.10)"}`,
            borderRadius: 12, padding: "9px 11px", marginTop: 8, flexShrink: 0,
            opacity: m.is_done ? 0.5 : 1, transition: "all 0.2s",
          }}>
            {/* Typed icon chip + label + time (per existing tag types) */}
            <div style={{ display: "flex", alignItems: "center", gap: 7, fontWeight: 600, fontSize: 11.5 }}>
              <span style={{
                width: 22, height: 22, borderRadius: 7, display: "grid", placeItems: "center",
                flexShrink: 0,
                background: `rgba(${tm.rgb},0.12)`,
                border: `1px solid rgba(${tm.rgb},0.35)`,
              }}>
                <TagIcon size={12} color={`rgba(${tm.rgb},0.9)`} strokeWidth={2} />
              </span>
              <span style={{ color: m.is_done ? "rgba(255,255,255,0.35)" : `rgba(${tm.rgb},0.95)` }}>{tm.label}</span>
              <time style={{ marginLeft: "auto", fontSize: 9.5, color: "rgba(255,255,255,0.35)", fontWeight: 500 }}>
                {niceTime(m.ts)}
              </time>
            </div>

            {m.senderName && (
              <p style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", margin: "5px 0 0" }}>
                from {m.senderName}
              </p>
            )}
            <p style={{
              fontSize: 11.5, color: "rgba(255,255,255,0.55)", margin: "5px 0 7px", lineHeight: 1.45,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              textDecoration: m.is_done ? "line-through" : "none",
            }}>
              {m.text || "(file attachment)"}
            </p>

            {/* View / Done / Delete pills → existing handlers */}
            <div style={{ display: "flex", gap: 5 }}>
              <Pill onClick={() => onView(m)}>View</Pill>
              <Pill onClick={() => onDone(m)}>{m.is_done ? "↩ Undo" : "✓ Done"}</Pill>
              <Pill onClick={() => onDelete(m.id)} danger>Delete</Pill>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Spec `.tb` outlined pill — green hover for View/Done, red hover for Delete.
function Pill({ onClick, danger, children }) {
  const [h, setH] = useState(false);
  const hoverBorder = danger ? "rgba(255,95,86,0.40)" : "rgba(0,255,153,0.30)";
  const hoverColor  = danger ? "#ff5f56" : "#00FF99";
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        fontSize: 10, fontWeight: 600, padding: "4px 10px", borderRadius: 99,
        background: "transparent",
        border: `1px solid ${h ? hoverBorder : "rgba(255,255,255,0.10)"}`,
        color: h ? hoverColor : "rgba(255,255,255,0.55)",
        cursor: "pointer", transition: "all 0.15s",
      }}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// EVENT DETAIL PANEL
// ─────────────────────────────────────────────────────────────

function EventDetailPanel({ event, onClose, onRsvp, onJoinKweryAI }) {
  const [busy,  setBusy]  = useState(false);
  const [error, setError] = useState("");
  const meeting = event?.resource || {};
  const attendees = meeting.attendees || [];
  const myRsvp    = meeting.my_rsvp || "pending";

  const isKweryAI  = meeting.meeting_link === "kweryai://";
  const isInPerson = !meeting.meeting_link;
  const isExternal = !!meeting.meeting_link && !isKweryAI;

  const start = event?.start ? new Date(event.start) : null;
  const end   = event?.end   ? new Date(event.end)   : null;
  const fmt = (d) => d ? d.toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

  const submitRsvp = async (rsvp) => {
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/meetings/${meeting.id}/rsvp`, {
        method:  "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization:  `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ rsvp }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onRsvp(rsvp);
    } catch (e) {
      setError(e.message || "Failed to update RSVP");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      marginTop: 8,
      borderRadius: 10,
      border: "1px solid rgba(0,255,153,0.25)",
      background: "rgba(0,255,153,0.04)",
      padding: 12,
      position: "relative",
    }}>
      <button
        onClick={onClose}
        style={{
          position: "absolute", top: 6, right: 6,
          width: 22, height: 22, borderRadius: "50%",
          background: "transparent", border: "none",
          color: "rgba(255,255,255,0.5)", cursor: "pointer",
          fontSize: 12,
        }}
        title="Close"
      >✕</button>

      <div style={{ color: ACCENT, fontWeight: 700, fontSize: 13, marginBottom: 4, paddingRight: 18 }}>
        {meeting.title || event?.title || "Meeting"}
      </div>

      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", marginBottom: 4 }}>
        {fmt(start)} → {end ? end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
      </div>

      {meeting.location && (
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
          <MapPin size={12} strokeWidth={1.9} style={{ flexShrink: 0 }} /> {meeting.location}
        </div>
      )}

      {isKweryAI && (
        <button
          onClick={() => onJoinKweryAI?.(String(meeting.id || "").slice(0, 8))}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            marginTop: 4, marginBottom: 8,
            padding: "4px 10px",
            borderRadius: 6,
            border: "1px solid rgba(0,255,153,0.4)",
            background: "rgba(0,255,153,0.12)",
            color: ACCENT,
            fontSize: 11,
            cursor: "pointer",
          }}
        >
          <Video size={13} strokeWidth={1.9} style={{ flexShrink: 0 }} /> Join KweryAI Call
        </button>
      )}

      {isExternal && (
        <a
          href={meeting.meeting_link}
          target="_blank"
          rel="noreferrer"
          style={{
            display: "inline-block",
            marginTop: 4, marginBottom: 8,
            padding: "4px 10px",
            borderRadius: 6,
            border: "1px solid rgba(0,255,153,0.4)",
            background: "rgba(0,255,153,0.12)",
            color: ACCENT,
            fontSize: 11,
            textDecoration: "none",
          }}
        >
          Join Meeting →
        </a>
      )}

      {/* isInPerson: no join button — Location field already shows the venue */}

      {attendees.length > 0 && (
        <div style={{ marginTop: 6, marginBottom: 8 }}>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginBottom: 2 }}>
            Attendees ({attendees.length})
          </div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", lineHeight: 1.4 }}>
            {attendees.map((a) => a.full_name || a.email || "Unknown").join(", ")}
          </div>
        </div>
      )}

      {myRsvp === "pending" && (
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          <button
            onClick={() => submitRsvp("accepted")}
            disabled={busy}
            style={{
              flex: 1, padding: "5px 0", borderRadius: 6,
              border: "1px solid rgba(0,255,153,0.4)",
              background: "rgba(0,255,153,0.12)",
              color: ACCENT, fontSize: 11, cursor: "pointer",
              opacity: busy ? 0.6 : 1,
            }}
          >
            Accept
          </button>
          <button
            onClick={() => submitRsvp("declined")}
            disabled={busy}
            style={{
              flex: 1, padding: "5px 0", borderRadius: 6,
              border: "1px solid rgba(255,80,80,0.35)",
              background: "rgba(255,80,80,0.08)",
              color: "#ff8080", fontSize: 11, cursor: "pointer",
              opacity: busy ? 0.6 : 1,
            }}
          >
            Decline
          </button>
        </div>
      )}

      {myRsvp !== "pending" && (
        <div style={{ marginTop: 6, fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
          You {myRsvp === "accepted" ? "accepted" : "declined"} this meeting.
        </div>
      )}

      {error && (
        <div style={{ marginTop: 6, fontSize: 11, color: "#ff8080" }}>
          {error}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SHARED FILES
// ─────────────────────────────────────────────────────────────

const fmtBytes = (b) => {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};
const shortDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString([], { day: "2-digit", month: "short" });
};
// Client-side bucketing on file_mime for the filter chips.
const fileKind = (mime) => {
  const m = (mime || "").toLowerCase();
  if (m.startsWith("image/")) return "images";
  if (/pdf|word|excel|spreadsheet|presentation|powerpoint|document|opendocument|text|csv|officedocument/.test(m)) return "docs";
  return "other";
};
// Typed chip — PDF red / XLS green / DOC blue / default amber (per spec).
const fileChip = (name, mime) => {
  const ext = ((name || "").split(".").pop() || "").toLowerCase();
  const m = (mime || "").toLowerCase();
  if (m.includes("pdf") || ext === "pdf")                                             return { label: "PDF", rgb: "255,95,86" };
  if (m.includes("sheet") || m.includes("excel") || ["xls", "xlsx", "csv"].includes(ext)) return { label: "XLS", rgb: "0,255,153" };
  if (m.includes("word") || m.includes("document") || ["doc", "docx"].includes(ext))  return { label: "DOC", rgb: "0,191,255" };
  return { label: (ext || "file").toUpperCase().slice(0, 3), rgb: "255,194,75" };
};

const SHARED_FILES_CSS = `
.kwf-row .kwf-actions{opacity:0;transition:.15s}
.kwf-row:hover .kwf-actions{opacity:1}
.kwf-row:hover{border-color:rgba(0,255,153,0.30)!important}
.kwf-th .kwf-ov{opacity:0;transition:.15s}
.kwf-th:hover .kwf-ov{opacity:1}
.kwf-th:hover{border-color:rgba(0,255,153,0.30)!important}
@keyframes kwfpulse{0%,100%{opacity:.35}50%{opacity:.7}}
.kwf-sk{background:#131313;border-radius:12px;animation:kwfpulse 1.2s ease-in-out infinite}
`;

const PAGE = 8;   // compact rail page size; "Load older files" appears when a full page returns

function SharedFiles({ peer, me, filesBus, onCount }) {
  const peerId    = peer?.id ? String(peer.id) : null;
  const peerName  = peer?.full_name || peer?.name || "";
  const peerFirst = (peerName || "").trim().split(/\s+/)[0] || "them";
  const myId      = String(me?.id || "");
  const token     = localStorage.getItem("token");

  const [files,       setFiles]       = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(false);
  const [nextBefore,  setNextBefore]  = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter,      setFilter]      = useState("all");
  const [confirmId,   setConfirmId]   = useState(null);
  const [goneIds,     setGoneIds]     = useState([]);
  const [imgFailed,   setImgFailed]   = useState([]);

  const dl = (u) => `${API_BASE}${u}`;   // relative authed redirect → absolute (same as FileAttachment)

  const load = useCallback((before) => {
    if (!peerId) return;
    const more = !!before;
    if (more) setLoadingMore(true); else { setLoading(true); setError(false); }
    const url = `${API_BASE}/api/dm/files?peer_id=${encodeURIComponent(peerId)}&limit=${PAGE}` +
      (before ? `&before=${encodeURIComponent(before)}` : "");   // encode: raw ISO +00:00 → space → 422
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => {
        const items = (d.files || []).map((f) => ({ ...f, message_id: String(f.message_id) }));
        setFiles((prev) => more ? [...prev, ...items] : items);
        setNextBefore(d.next_before || null);
      })
      .catch(() => { if (!more) setError(true); })
      .finally(() => more ? setLoadingMore(false) : setLoading(false));
  }, [peerId, token]);

  // Fetch on selected-conversation change.
  useEffect(() => {
    setFiles([]); setNextBefore(null); setConfirmId(null); setGoneIds([]); setImgFailed([]); setFilter("all");
    if (peerId) load();
  }, [peerId, load]);

  // Live updates from the thread's WebSocket (bridged via filesBus ref).
  useEffect(() => {
    if (!filesBus) return;
    filesBus.current = {
      onFileMessage: (d) => {
        if (!peerId) return;
        const sid = String(d.sender_id ?? "");
        const rid = String(d.receiver_id ?? "");
        if (sid !== peerId && rid !== peerId) return;                // not this conversation
        if (!(d.file_url || d.download_url) || !d.file_name) return; // not a file message
        const item = {
          message_id:  String(d.id ?? d.message_id),
          file_name:   d.file_name,
          file_size:   d.file_size,
          file_mime:   d.file_mime || "",
          file_type:   d.file_type,
          uploader_id: String(d.sender_id ?? d.uploader_id ?? ""),
          created_at:  d.created_at || d.ts,
          download_url: d.file_url || d.download_url,
        };
        setFiles((prev) => prev.some((f) => f.message_id === item.message_id) ? prev : [item, ...prev]);
      },
      onFileDeleted: (id) => {
        const mid = String(id);
        setFiles((prev) => prev.filter((f) => f.message_id !== mid));
      },
    };
    return () => { if (filesBus.current) filesBus.current = null; };
  }, [filesBus, peerId]);

  const doDelete = (id) => {
    fetch(`${API_BASE}/api/dm/files/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        setConfirmId(null);
        setGoneIds((prev) => [...prev, id]);                       // animate row out
        setTimeout(() => setFiles((prev) => prev.filter((f) => f.message_id !== id)), 240);
      })
      .catch(() => { setConfirmId(null); toast.error("Couldn't delete file — it was restored"); });
  };

  const live    = files.filter((f) => !goneIds.includes(f.message_id));
  useEffect(() => { onCount?.(live.length); }, [live.length, onCount]);
  const isImage = (f) => fileKind(f.file_mime) === "images" && !imgFailed.includes(f.message_id);
  const shown   = filter === "all" ? live : live.filter((f) => fileKind(f.file_mime) === filter);
  const images  = shown.filter(isImage);
  const rows    = shown.filter((f) => !isImage(f));

  if (!peerId) return (
    <p style={{ color: "rgba(255,255,255,0.25)", fontSize: 11, textAlign: "center", padding: "10px 0" }}>
      Select a conversation to see shared files
    </p>
  );

  return (
    <>
      <style>{SHARED_FILES_CSS}</style>
      <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.35)", margin: "-2px 0 10px" }}>
        with {peerName || "this conversation"}
      </div>

      {/* Filter chips — omitted when ≤ 3 files */}
      {live.length > 3 && (
        <div style={{ display: "flex", gap: 5, marginBottom: 10 }}>
          {[["all", "All"], ["images", "Images"], ["docs", "Docs"], ["other", "Other"]].map(([k, lbl]) => {
            const on = filter === k;
            return (
              <span key={k} onClick={() => setFilter(k)}
                style={{
                  fontSize: 10, fontWeight: 600, padding: "4px 11px", borderRadius: 999, cursor: "pointer",
                  background: on ? "#00FF99" : "#131313",
                  border: `1px solid ${on ? "#00FF99" : "rgba(255,255,255,0.10)"}`,
                  color: on ? "#000" : "rgba(255,255,255,0.55)",
                }}>{lbl}</span>
            );
          })}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {[0, 1, 2].map((i) => <div key={i} className="kwf-sk" style={{ height: 46 }} />)}
        </div>
      )}

      {/* Error + retry */}
      {!loading && error && (
        <div style={{ textAlign: "center", padding: "8px 0" }}>
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 11, marginBottom: 8 }}>Couldn't load files</p>
          <button onClick={() => load()}
            style={{ fontSize: 10.5, fontWeight: 600, padding: "5px 14px", borderRadius: 999, cursor: "pointer",
              background: "transparent", border: "1px solid rgba(0,255,153,0.30)", color: "#00FF99" }}>
            Retry
          </button>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && live.length === 0 && (
        <p style={{ color: "rgba(255,255,255,0.25)", fontSize: 11, textAlign: "center", padding: "10px 0" }}>
          No files shared yet
        </p>
      )}

      {/* Images */}
      {!loading && !error && images.length > 0 && (
        <>
          <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: ".09em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", margin: "2px 0 6px" }}>
            Images · {images.length}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 5 }}>
            {images.map((f) => {
              const confirming = confirmId === f.message_id;
              const gone = goneIds.includes(f.message_id);
              return (
                <div key={f.message_id} className="kwf-th"
                  style={{ position: "relative", aspectRatio: "1", borderRadius: 10, border: "1px solid rgba(255,255,255,0.10)",
                    overflow: "hidden", background: "#131313", opacity: gone ? 0 : 1, transition: "opacity .2s" }}>
                  <img src={dl(f.download_url)} alt={f.file_name}
                    onError={() => setImgFailed((p) => p.includes(f.message_id) ? p : [...p, f.message_id])}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  <div className="kwf-ov" style={{
                    position: "absolute", inset: 0, background: confirming ? "rgba(255,95,86,0.35)" : "rgba(0,0,0,0.55)",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 5, opacity: confirming ? 1 : undefined,
                  }}>
                    {confirming ? (
                      <>
                        <button onClick={() => doDelete(f.message_id)} style={thBtn("255,95,86")}>Del</button>
                        <button onClick={() => setConfirmId(null)} style={thBtn("255,255,255")}>Keep</button>
                      </>
                    ) : (
                      <>
                        <a href={dl(f.download_url)} target="_blank" rel="noreferrer" title="Download" style={thIcon()}><Download size={12} strokeWidth={1.9} /></a>
                        <span onClick={() => setConfirmId(f.message_id)} title="Delete" style={thIcon(true)}><Trash2 size={12} strokeWidth={1.9} /></span>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Typed file rows */}
      {!loading && !error && rows.length > 0 && (
        <>
          {images.length > 0 && (
            <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: ".09em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", margin: "12px 0 2px" }}>
              Files · {rows.length}
            </div>
          )}
          {rows.map((f) => {
            const confirming = confirmId === f.message_id;
            const gone = goneIds.includes(f.message_id);
            const chip = fileChip(f.file_name, f.file_mime);
            const mine = f.uploader_id === myId;
            return (
              <div key={f.message_id} className="kwf-row"
                style={{
                  display: "flex", alignItems: "center", gap: 9, borderRadius: 12, padding: "8px 9px", marginTop: 6,
                  background: confirming ? "rgba(255,95,86,0.06)" : "#131313",
                  border: `1px solid ${confirming ? "rgba(255,95,86,0.4)" : "rgba(255,255,255,0.10)"}`,
                  opacity: gone ? 0 : 1, transform: gone ? "translateX(10px)" : "none",
                  transition: "opacity .2s, transform .2s",
                }}>
                <span style={{
                  width: 30, height: 30, borderRadius: 9, display: "grid", placeItems: "center", flexShrink: 0,
                  fontSize: 9, fontWeight: 800, letterSpacing: ".03em",
                  background: `rgba(${chip.rgb},0.12)`, border: `1px solid rgba(${chip.rgb},0.35)`, color: `rgb(${chip.rgb})`,
                }}>{chip.label}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    color: confirming ? "#ff5f56" : "#fff" }}>{f.file_name}</div>
                  <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.35)", marginTop: 1 }}>
                    {fmtBytes(f.file_size)} · {shortDate(f.created_at)} · {mine ? "you" : peerFirst}
                  </div>
                </div>
                {confirming ? (
                  <div style={{ display: "flex", gap: 5 }}>
                    <button onClick={() => doDelete(f.message_id)} style={cfBtn("255,95,86")}>Delete</button>
                    <button onClick={() => setConfirmId(null)} style={cfBtn("255,255,255")}>Keep</button>
                  </div>
                ) : (
                  <div className="kwf-actions" style={{ display: "flex", gap: 4 }}>
                    <a href={dl(f.download_url)} target="_blank" rel="noreferrer" title="Download" style={fbIcon()}><Download size={12} strokeWidth={1.9} /></a>
                    <span onClick={() => setConfirmId(f.message_id)} title="Delete" style={fbIcon(true)}><Trash2 size={12} strokeWidth={1.9} /></span>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {/* Pagination */}
      {!loading && !error && nextBefore && (
        <button onClick={() => load(nextBefore)} disabled={loadingMore}
          style={{ width: "100%", marginTop: 9, background: "transparent", border: "1px dashed rgba(255,255,255,0.10)",
            color: "rgba(255,255,255,0.55)", borderRadius: 999, padding: 7, fontSize: 10.5, fontWeight: 600,
            cursor: loadingMore ? "default" : "pointer" }}>
          {loadingMore ? "Loading…" : "Load older files"}
        </button>
      )}

      {!loading && !error && live.length > 0 && (
        <div style={{ fontSize: 9, color: "rgba(255,255,255,0.3)", textAlign: "center", marginTop: 8, lineHeight: 1.5 }}>
          Hover a file for actions · deleting removes it for both people and can't be undone
        </div>
      )}
    </>
  );
}

// Small action-button style helpers for the file rows / thumbs.
const fbIcon = (danger) => ({
  width: 26, height: 26, borderRadius: 99, display: "grid", placeItems: "center", cursor: "pointer",
  background: "transparent", border: "1px solid rgba(255,255,255,0.10)", textDecoration: "none",
  fontSize: 12, color: danger ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.55)",
});
const thIcon = (danger) => ({
  width: 24, height: 24, borderRadius: 99, display: "grid", placeItems: "center", cursor: "pointer",
  background: "rgba(13,13,13,0.9)", border: "1px solid rgba(255,255,255,0.15)", textDecoration: "none",
  fontSize: 10, color: "#fff",
});
const thBtn = (rgb) => ({
  fontSize: 9, fontWeight: 700, padding: "4px 8px", borderRadius: 99, cursor: "pointer", fontFamily: "inherit",
  background: "rgba(13,13,13,0.9)", border: `1px solid rgba(${rgb},0.6)`, color: `rgb(${rgb})`,
});
const cfBtn = (rgb) => ({
  fontSize: 9.5, fontWeight: 600, padding: "4px 10px", borderRadius: 99, cursor: "pointer", fontFamily: "inherit",
  background: "transparent", border: `1px solid rgba(${rgb},0.4)`, color: `rgb(${rgb})`,
});

// ─────────────────────────────────────────────────────────────
// MAIN RIGHT RAIL
// ─────────────────────────────────────────────────────────────

export default function RightRail({ onNavigate, onMarkDone, activePeer, filesBus }) {
  const { tagged: ctxTagged, dismissTaggedMessage } = useTaggedMessages();
  const { user } = useContext(AuthContext);
  const [apiTagged,      setApiTagged]      = useState([]);
  const [localDismissed, setLocalDismissed] = useState(new Set());

  // Calendar state
  const [calDate,           setCalDate]           = useState(new Date());
  const [events,            setEvents]            = useState([]);
  const [fetchedRange,      setFetchedRange]      = useState({ startMs: null, endMs: null }); // months currently in `events`
  const [calLoading,        setCalLoading]        = useState(false);
  const [showMeetingModal,  setShowMeetingModal]  = useState(false);
  const [selectedSlot,      setSelectedSlot]      = useState(null);
  const [selectedEvent,     setSelectedEvent]     = useState(null);

  // "Join KweryAI Call" on a meeting routes through the app-wide CallProvider
  // (single call modal shared with the rest of the app).
  const { joinRoom } = useCallContext();

  // ── Collapsible-card state — persisted per-user in localStorage ──
  const uid = user?.id || "anon";
  const [collapsed, setCollapsed] = useState({});
  const [fileCount, setFileCount] = useState(0);   // shared-files count for the header badge
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`kwery_msgr_collapsed_${uid}`);
      setCollapsed(raw ? JSON.parse(raw) : {});
    } catch { setCollapsed({}); }
  }, [uid]);
  const toggleCard = (key) => setCollapsed((prev) => {
    const next = { ...prev, [key]: !prev[key] };
    try { localStorage.setItem(`kwery_msgr_collapsed_${uid}`, JSON.stringify(next)); } catch {}
    return next;
  });

  // ── Calendar view state — 'day' | 'week' | 'month', persisted per-user ──
  const [calView,      setCalView]      = useState("week");
  const [selectedHour, setSelectedHour] = useState(null); // empty-slot pick in Day view
  useEffect(() => {
    try {
      const v = localStorage.getItem(`kwery_msgr_calview_${uid}`);
      if (v === "day" || v === "week" || v === "month") setCalView(v);
    } catch {}
  }, [uid]);
  const changeView = (v) => {
    setSelectedHour(null); // switching view clears the empty-slot selection
    setCalView(v);
    try { localStorage.setItem(`kwery_msgr_calview_${uid}`, v); } catch {}
  };
  // ‹ › step by the current view's unit. Month steps anchor to the 1st to avoid
  // day-overflow (e.g. Jan 31 → Feb). The covering-window effect below only
  // refetches when the new visible range falls outside what's already fetched.
  const stepNav = (dir) => {
    setSelectedHour(null);
    setCalDate((d) => {
      if (calView === "day")  return addDays(d, dir);
      if (calView === "week") return addDays(d, dir * 7);
      const x = startOfMonth(d); x.setMonth(x.getMonth() + dir); return x;
    });
  };
  const goToday = () => { setSelectedHour(null); setCalDate(new Date()); };
  // Drill from a week/month day cell into that day's hourly view.
  const openDay = (dayDate) => { setSelectedHour(null); setCalDate(dayDate); changeView("day"); };

  // ── Tagged Messages ────────────────────────────────────────
  const loadTagged = useCallback(() => {
    if (!user) return;
    fetch(`${API_BASE}/api/dm/tagged`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((r) => (r.ok ? r.json() : { tagged: [] }))
      .then((d) => {
        const msgs = d?.tagged || [];
        setApiTagged(msgs.map((m) => ({
          id:         m.id,
          sender_id:  m.sender_id,
          text:       m.text || "(file)",
          tag:        m.tag,
          senderName: m.sender_name || "Someone",
          ts:         m.ts,
          is_done:    m.is_done || false,
        })));
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => { loadTagged(); }, [loadTagged]);

  const ctxIds = new Set(ctxTagged.map((m) => m.id));
  const tagged = [
    ...ctxTagged,
    ...apiTagged.filter((m) => !ctxIds.has(m.id)),
  ].filter((m) => !localDismissed.has(m.id) && !m.is_done);

  const token = localStorage.getItem("token");

  const handleView = (m) => onNavigate?.(m);

  const handleDone = (m) => {
    if (m.is_done) {
      setApiTagged((prev) => prev.map((x) => (x.id === m.id ? { ...x, is_done: false } : x)));
      return;
    }
    setApiTagged((prev) => prev.map((x) => (x.id === m.id ? { ...x, is_done: true } : x)));
    onMarkDone?.current?.done?.(m.id);
    fetch(`${API_BASE}/api/dm/${m.id}/done`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
    setTimeout(() => {
      setLocalDismissed((prev) => new Set([...prev, m.id]));
    }, 800);
  };

  const handleDelete = (id) => {
    setLocalDismissed((prev) => new Set([...prev, id]));
    dismissTaggedMessage(id);
    onMarkDone?.current?.deleted?.(id);
    fetch(`${API_BASE}/api/dm/${id}/delete-tag`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  };

  // ── Calendar ───────────────────────────────────────────────
  // Fetch whole months covering [startExcl, endExcl) and record what we hold in
  // `fetchedRange`. Callers align `endExcl` to a month boundary so a straddling
  // week pulls both months in one request; the covering effect below skips the
  // fetch whenever the visible range already sits inside `fetchedRange`.
  const fetchRange = useCallback((coverStart, coverEndExcl) => {
    setCalLoading(true);
    const start = coverStart.toISOString();
    const end   = new Date(coverEndExcl.getTime() - 1).toISOString(); // inclusive last instant
    fetch(`${API_BASE}/api/meetings/?start=${start}&end=${end}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        const list = Array.isArray(d) ? d : (d?.data || []);
        setEvents(list.map((m) => ({
          id:       m.id,
          title:    m.title,
          start:    new Date(m.start_time),
          end:      new Date(m.end_time),
          resource: m,
        })));
        setFetchedRange({ startMs: coverStart.getTime(), endMs: coverEndExcl.getTime() });
      })
      .catch(() => setEvents([]))
      .finally(() => setCalLoading(false));
  }, []);

  const handleRsvp = (rsvp) => {
    if (!selectedEvent) return;
    setEvents((prev) => prev.map((e) =>
      e.id === selectedEvent.id
        ? { ...e, resource: { ...e.resource, my_rsvp: rsvp } }
        : e
    ));
    setSelectedEvent((prev) => prev
      ? { ...prev, resource: { ...prev.resource, my_rsvp: rsvp } }
      : prev
    );
  };

  // ── Calendar derived values (every view reads the month-fetched `events`) ──
  const today     = new Date();
  const weekStart = startOfWeek(calDate);
  const weekDays  = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekEndEx = addDays(weekStart, 7);
  const weekEvents = events
    .filter((e) => e.start >= weekStart && e.start < weekEndEx)
    .sort((a, b) => a.start - b.start);
  const dayHasEvents = (d) => events.some((e) => sameDate(e.start, d));

  // Day view — events on calDate grouped into hourly rows. Default band 8–18,
  // extended to include any event that starts earlier or ends later.
  const dayEvents = events
    .filter((e) => sameDate(e.start, calDate))
    .sort((a, b) => a.start - b.start);
  let dayStartH = 8, dayEndH = 18; // dayEndH exclusive
  dayEvents.forEach((e) => {
    dayStartH = Math.min(dayStartH, e.start.getHours());
    const eh = e.end ? e.end.getHours() + (e.end.getMinutes() > 0 ? 1 : 0) : e.start.getHours() + 1;
    dayEndH = Math.max(dayEndH, eh);
  });
  dayStartH = Math.max(0, dayStartH);
  dayEndH   = Math.min(24, Math.max(dayEndH, dayStartH + 1));
  const dayHours = [];
  for (let h = dayStartH; h < dayEndH; h++) dayHours.push(h);
  const eventsAtHour = (h) => dayEvents.filter((e) => e.start.getHours() === h);

  // Month view — 6-week (42-cell) grid anchored to the week holding the 1st.
  const monthGridStart = startOfWeek(startOfMonth(calDate));
  const monthCells = Array.from({ length: 42 }, (_, i) => addDays(monthGridStart, i));

  // ── Widened fetch window ────────────────────────────────────
  // Visible data range for the active view, half-open [visStart, visEnd).
  const visStart =
    calView === "day"  ? startOfDay(calDate) :
    calView === "week" ? weekStart :
                         startOfMonth(calDate);
  const visEnd =
    calView === "day"  ? addDays(startOfDay(calDate), 1) :
    calView === "week" ? weekEndEx :
                         startOfNextMonth(calDate);
  // Month-align the fetch so small steps inside a month reuse the same window,
  // while a straddling week pulls both months in a single request.
  const coverStart   = startOfMonth(visStart);
  const coverEndExcl = startOfNextMonth(addDays(visEnd, -1));
  const coverKey     = `${coverStart.getTime()}_${coverEndExcl.getTime()}`;

  useEffect(() => {
    // No refetch when the visible range already sits inside what we hold
    // (e.g. drilling into one month of an already-fetched straddling week).
    if (fetchedRange.startMs != null &&
        visStart.getTime() >= fetchedRange.startMs &&
        visEnd.getTime()   <= fetchedRange.endMs) {
      return;
    }
    fetchRange(coverStart, coverEndExcl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverKey]);

  // Force a refetch of the current window (after a meeting is created).
  const refreshEvents = () => fetchRange(coverStart, coverEndExcl);

  // Nav label + schedule CTA follow the active view.
  const navLabel =
    calView === "day"
      ? `${calDate.toLocaleDateString([], { weekday: "long" })} ${calDate.getDate()} ${calDate.toLocaleDateString([], { month: "long" })}`
      : calView === "week"
      ? weekRangeLabel(weekStart)
      : `${calDate.toLocaleDateString([], { month: "long" })} ${calDate.getFullYear()}`;
  const schedLabel =
    calView === "day" && selectedHour != null
      ? `+ Schedule meeting at ${selectedHour}:00`
      : "+ Schedule meeting";
  const isGreenEvent = (e) => e.resource?.my_rsvp === "accepted";

  // Open MeetingModal; a picked Day-view empty hour prefills that slot
  // (restores react-big-calendar's onSelectSlot behaviour).
  const openScheduler = () => {
    if (calView === "day" && selectedHour != null) {
      const s = new Date(calDate); s.setHours(selectedHour, 0, 0, 0);
      const e = new Date(s);       e.setHours(selectedHour + 1);
      setSelectedSlot({ start: s, end: e });
    } else {
      setSelectedSlot(null);
    }
    setSelectedEvent(null);
    setShowMeetingModal(true);
  };
  const openEvent = (e) => { setSelectedHour(null); setSelectedEvent(e); };

  const navBtnStyle = {
    width: 24, height: 24, borderRadius: 8, flexShrink: 0, display: "grid", placeItems: "center",
    cursor: "pointer", background: "#131313", border: "1px solid rgba(255,255,255,0.10)", color: "rgba(255,255,255,0.55)",
  };
  const todayBtnStyle = {
    fontSize: 9.5, fontWeight: 600, padding: "4px 10px", borderRadius: 99, cursor: "pointer",
    background: "transparent", border: "1px solid rgba(255,255,255,0.10)", color: "rgba(255,255,255,0.55)",
  };

  return (
    <div className="p-4 space-y-4">
      {/* Tagged messages — collapsible card */}
      <Card title="Tagged messages" open={!collapsed.tagged} onToggle={() => toggleCard("tagged")}>
        <TaggedSection
          tagged={tagged}
          onView={handleView}
          onDone={handleDone}
          onDelete={handleDelete}
        />
      </Card>

      {/* Calendar — collapsible card with interactive Day / Week / Month views.
          calDate drives a month-aligned covering fetch; all three views read
          that data. Event click → EventDetailPanel / RSVP; empty Day-view hour
          → prefilled MeetingModal (old onSelectSlot). Collapse key unchanged. */}
      <Card title="Calendar" open={!collapsed.calendar} onToggle={() => toggleCard("calendar")}>
        {/* Segmented view tabs */}
        <div style={{ display: "flex", gap: 4, background: "#131313", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 999, padding: 3, marginBottom: 10 }}>
          {["day", "week", "month"].map((v) => {
            const on = calView === v;
            return (
              <span key={v} onClick={() => changeView(v)}
                style={{
                  flex: 1, textAlign: "center", fontSize: 10.5, fontWeight: 600,
                  padding: "5px 0", borderRadius: 999, cursor: "pointer",
                  background: on ? "#00FF99" : "transparent",
                  color: on ? "#000" : "rgba(255,255,255,0.55)",
                }}>
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </span>
            );
          })}
        </div>

        {/* Nav row — ‹ › step by the view's unit, label follows view, Today resets */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
          <button onClick={() => stepNav(-1)} aria-label="Previous" style={navBtnStyle}><ChevronLeft size={12} /></button>
          <span style={{ flex: 1, textAlign: "center", fontSize: 11.5, fontWeight: 600, color: "#fff" }}>{navLabel}</span>
          <button onClick={() => stepNav(1)} aria-label="Next" style={navBtnStyle}><ChevronRight size={12} /></button>
          <button onClick={goToday} style={todayBtnStyle}>Today</button>
        </div>

        {calLoading && (
          <div style={{ textAlign: "center", color: "rgba(255,255,255,0.4)", fontSize: 11, marginBottom: 8 }}>Loading…</div>
        )}

        {/* WEEK */}
        {calView === "week" && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 3, marginBottom: 8, textAlign: "center", fontSize: 9.5, color: "rgba(255,255,255,0.35)" }}>
              {weekDays.map((d, i) => {
                const isToday = sameDate(d, today);
                const has = dayHasEvents(d);
                return (
                  <div key={i}>
                    {DOW_LABELS[i]}
                    <b onClick={() => openDay(d)}
                      style={{
                        display: "block", fontSize: 12, fontWeight: 600, marginTop: 2,
                        padding: "4px 0", borderRadius: 8, cursor: "pointer",
                        color: isToday ? "#000" : "#fff",
                        background: isToday ? "#00FF99" : "transparent",
                        boxShadow: !isToday && has ? "inset 0 -2px 0 #00FF99" : "none",
                      }}>{d.getDate()}</b>
                  </div>
                );
              })}
            </div>
            {weekEvents.length === 0 ? (
              <div style={{ textAlign: "center", color: "rgba(255,255,255,0.35)", fontSize: 11, padding: "14px 0" }}>No meetings this week</div>
            ) : (
              weekEvents.map((e) => (
                <div key={e.id} onClick={() => openEvent(e)}
                  style={{ display: "flex", gap: 8, alignItems: "center", background: "#131313", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 11, padding: "8px 10px", marginTop: 6, fontSize: 11.5, cursor: "pointer" }}>
                  <span style={{ width: 3, height: 26, borderRadius: 99, flexShrink: 0, background: isGreenEvent(e) ? "#00FF99" : "#00BFFF" }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.title || "Meeting"}</div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)" }}>
                      {e.start.toLocaleDateString([], { weekday: "short" })} · {e.start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      {e.end ? ` – ${e.end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}
                    </div>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {/* DAY — hourly rows; empty hour picks a slot, event opens detail */}
        {calView === "day" && (
          <div style={{ maxHeight: 250, overflowY: "auto", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 12 }}>
            {dayHours.map((h, idx) => {
              const evs = eventsAtHour(h);
              const hasEv = evs.length > 0;
              const isSel = selectedHour === h;
              return (
                <div key={h}
                  onClick={hasEv ? undefined : () => setSelectedHour(isSel ? null : h)}
                  style={{
                    display: "flex", minHeight: 34,
                    borderBottom: idx === dayHours.length - 1 ? "none" : "1px dashed rgba(255,255,255,0.06)",
                    cursor: hasEv ? "default" : "pointer",
                    background: isSel ? "rgba(0,255,153,0.12)" : "transparent",
                    boxShadow: isSel ? "inset 2px 0 0 #00FF99" : "none",
                    transition: "background 0.12s",
                  }}
                  onMouseEnter={(ev) => { if (!hasEv && !isSel) ev.currentTarget.style.background = "rgba(0,255,153,0.05)"; }}
                  onMouseLeave={(ev) => { if (!hasEv && !isSel) ev.currentTarget.style.background = "transparent"; }}>
                  <span style={{ width: 52, flexShrink: 0, fontSize: 9.5, color: "rgba(255,255,255,0.35)", padding: "6px 8px" }}>{hourLabel(h)}</span>
                  <span style={{ flex: 1, padding: "4px 6px 4px 0", display: "flex", flexDirection: "column", gap: 4 }}>
                    {evs.map((e) => {
                      const green = isGreenEvent(e);
                      return (
                        <span key={e.id} onClick={(ev) => { ev.stopPropagation(); openEvent(e); }}
                          style={{
                            background: "#131313",
                            border: `1px solid ${green ? "rgba(0,255,153,0.30)" : "rgba(0,191,255,0.30)"}`,
                            borderLeft: `3px solid ${green ? "#00FF99" : "#00BFFF"}`,
                            borderRadius: 8, padding: "5px 8px", fontSize: 10.5, fontWeight: 600, cursor: "pointer",
                          }}>
                          {e.title || "Meeting"}
                          <span style={{ display: "block", fontSize: 9, color: "rgba(255,255,255,0.35)", fontWeight: 500 }}>
                            {e.start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            {e.end ? ` – ${e.end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}
                          </span>
                        </span>
                      );
                    })}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* MONTH — mini grid, dim adjacent days, today highlight, event dot */}
        {calView === "month" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2, textAlign: "center" }}>
            {DOW_LABELS.map((l, i) => (
              <span key={`h${i}`} style={{ fontSize: 9, color: "rgba(255,255,255,0.35)", padding: "3px 0" }}>{l}</span>
            ))}
            {monthCells.map((d, i) => {
              const dim = d.getMonth() !== calDate.getMonth();
              const isToday = sameDate(d, today);
              const has = !dim && dayHasEvents(d);
              return (
                <span key={i} onClick={() => openDay(d)}
                  style={{
                    position: "relative", fontSize: 11, fontWeight: dim ? 400 : 600,
                    padding: "6px 0 4px", borderRadius: 9, cursor: "pointer",
                    color: isToday ? "#000" : dim ? "rgba(255,255,255,0.35)" : "#fff",
                    background: isToday ? "#00FF99" : "transparent",
                  }}
                  onMouseEnter={(ev) => { if (!isToday) ev.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                  onMouseLeave={(ev) => { if (!isToday) ev.currentTarget.style.background = "transparent"; }}>
                  {d.getDate()}
                  {has && <span style={{ position: "absolute", bottom: 2, left: "50%", transform: "translateX(-50%)", width: 4, height: 4, borderRadius: 99, background: isToday ? "#000" : "#00FF99" }} />}
                </span>
              );
            })}
          </div>
        )}

        {/* Schedule CTA — text reflects a picked Day-view hour */}
        <button onClick={openScheduler}
          style={{ width: "100%", marginTop: 10, background: "transparent", border: "1px solid rgba(0,255,153,0.30)", color: "#00FF99", borderRadius: 999, padding: 9, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
          onMouseEnter={(ev) => (ev.currentTarget.style.background = "rgba(0,255,153,0.12)")}
          onMouseLeave={(ev) => (ev.currentTarget.style.background = "transparent")}>
          {schedLabel}
        </button>
        <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.35)", textAlign: "center", marginTop: 8 }}>
          Click a day to open it · click an empty hour to schedule at that time
        </div>

        {selectedEvent && (
          <EventDetailPanel
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
            onRsvp={handleRsvp}
            onJoinKweryAI={(roomId) => {
              joinRoom({
                room_id:       roomId,
                targetUser:    { id: selectedEvent.resource.id, full_name: selectedEvent.resource.title },
                audioOnly:     false,
                meeting_id:    selectedEvent.resource.id,
                meeting_title: selectedEvent.resource.title,
              });
            }}
          />
        )}
      </Card>

      {/* Shared files — collapsible card below the Calendar, per selected peer */}
      <Card
        title="Shared files"
        open={!collapsed.files}
        onToggle={() => toggleCard("files")}
        badge={fileCount > 0 ? fileCount : null}
      >
        <SharedFiles peer={activePeer} me={user} filesBus={filesBus} onCount={setFileCount} />
      </Card>

      <MeetingModal
        open={showMeetingModal}
        onClose={() => {
          setShowMeetingModal(false);
          setSelectedSlot(null);
        }}
        onSuccess={() => {
          setShowMeetingModal(false);
          setSelectedSlot(null);
          refreshEvents();
        }}
        initialSlot={selectedSlot}
      />

    </div>
  );
}

// Collapsible card — header (dot + title + optional count badge + chevron);
// collapsed = header only.
const Card = ({ title, open, onToggle, badge, children }) => (
  <div style={{ background: "#0d0d0d", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 18, padding: 14 }}>
    <div onClick={onToggle} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 600, cursor: "pointer", userSelect: "none" }}>
      <span style={{ width: 8, height: 8, borderRadius: 99, background: "#0d0d0d", border: "2px solid #00FF99", boxShadow: "0 0 7px rgba(0,255,153,0.4)", flexShrink: 0 }} />
      <span style={{ color: "#fff" }}>{title}</span>
      {badge != null && (
        <span style={{ fontSize: 10, fontWeight: 600, color: "#00FF99", background: "rgba(0,255,153,0.12)",
          border: "1px solid rgba(0,255,153,0.30)", borderRadius: 99, padding: "2px 8px" }}>{badge}</span>
      )}
      <span style={{ marginLeft: "auto", fontSize: 9, color: "rgba(255,255,255,0.35)", transform: open ? "none" : "rotate(-90deg)", transition: "0.15s" }}>▼</span>
    </div>
    {open && <div style={{ marginTop: 8 }}>{children}</div>}
  </div>
);
